/**
 * Treatment Record — Charting & Files drawer (PRD.182).
 *
 * Right drawer for the heavier charting/visual flows. The drawer body is the
 * `DentalChartingHero`, which provides All / Tooth Chart / Images /
 * Before-After / Draw tabs with icons — so the drawer intentionally has no
 * outer tab strip to avoid duplication.
 *
 * The header includes a `DateChipPopover` so the user can change the target
 * date inline; switching dates re-resolves the underlying Visit Note via
 * `ensureEncounterForDate` (creating one in the background if none exists).
 *
 * Drawer width follows the configured Treatment Record drawer width preset.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import { useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { ensureEncounterForDate } from '../../../services/encounter-service';
import { DentalChartingHero } from './DentalChartingHero';
import { DateChipPopover } from './DateChipPopover';
import {
  qEncounterById,
  type PatientEncounterRow,
  type TreatmentRecordDrawerWidth,
} from '@helios/data-access';

export const DRAWER_WIDTH_BY_PRESET: Record<TreatmentRecordDrawerWidth, string> = {
  standard: 'min(540px, 90vw)',
  wide: 'min(820px, 92vw)',
  expanded: 'min(1200px, 96vw)',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  initialEncounterId: string | null;
  initialDate: string | null;
  drawerWidth: TreatmentRecordDrawerWidth;
  /** Initial inner tab — used when drilling in from a saved charting. */
  initialTab?: string;
  initialBeforeAfterChartId?: string | null;
  initialDrawChartId?: string | null;
  /** Hide the date chip — drill-ins from a specific chart shouldn't allow date switching. */
  hideDateChip?: boolean;
}

export function TreatmentChartingFilesDrawer({
  open,
  onOpenChange,
  patientId,
  initialEncounterId,
  initialDate,
  drawerWidth,
  initialTab,
  initialBeforeAfterChartId,
  initialDrawChartId,
  hideDateChip = false,
}: Props) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const [encounterId, setEncounterId] = useState<string | null>(initialEncounterId);
  const [date, setDate] = useState<string | null>(initialDate);
  const [resolving, setResolving] = useState(false);

  // Sync from props when the drawer is (re)opened with a new initial value.
  useEffect(() => {
    if (open) {
      setEncounterId(initialEncounterId);
      setDate(initialDate);
    }
  }, [open, initialEncounterId, initialDate]);

  const handleDateChange = async (newDate: string) => {
    if (!db || !orgId || !patientId) return;
    if (newDate === date) return;
    setDate(newDate);
    setResolving(true);
    try {
      const { encounterId: newEncounterId } = await ensureEncounterForDate({
        db,
        orgId,
        patientId,
        date: newDate,
      });
      setEncounterId(newEncounterId);
    } catch (err) {
      console.error('[TreatmentChartingFilesDrawer] resolve encounter:', err);
    } finally {
      setResolving(false);
    }
  };

  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const visitId = encounterRows[0]?.visit_id ?? null;
  const ready = !!encounterId && !!visitId && !resolving;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Add Charting & Files"
      portalStyle={brandTokens as CSSProperties}
      width={DRAWER_WIDTH_BY_PRESET[drawerWidth]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {!hideDateChip && date && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '8px var(--sp-5)',
              borderBottom: '1px solid var(--border)',
              background: 'var(--bg-surface)',
            }}
          >
            <DateChipPopover patientId={patientId} date={date} onChange={handleDateChange} />
          </div>
        )}
        {!ready ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {resolving ? 'Switching date…' : 'Loading…'}
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <DentalChartingHero
              key={encounterId ?? ''}
              patientId={patientId}
              encounterId={encounterId!}
              visitId={visitId!}
              primary={primary}
              initialTab={initialTab}
              initialBeforeAfterChartId={initialBeforeAfterChartId}
              initialDrawChartId={initialDrawChartId}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
}
